import { cn } from '@navinc/base-react-components'
import { LoaderFunctionArgs } from '@remix-run/node'
import { type MetaFunction, useLoaderData, useRouteLoaderData } from '@remix-run/react'
import { ReactNode } from 'react'
import { SitemapFunction } from 'remix-sitemap'

import { withCache } from '../../.server/cache.js'
import { Blocks } from '../../_blocks/Blocks.js'
import { Footer } from '../../_components/Footer/index.js'
import { Header } from '../../_components/Header/index.js'
import { Hero, heroFragment } from '../../_components/Hero/index.js'
import { OffersProvider } from '../../_components/OffersProvider/offers-provider.jsx'
import { InteractionContextProvider } from '../../_providers/InteractionContextProvider/InteractionContextProvider.jsx'
import { isPreviewMode } from '../../_utilities/preview-mode.server.js'
import { pagesCollection } from '../../const.js'
import { cmsGqlRequest } from '../../lib/cms/cms.server.js'
import { getFragmentData } from '../../lib/cms/index.js'
import { captureError } from '../../lib/error-reporting.js'
import { pagePathname } from '../../lib/page-pathname.js'
import { promiseValueErrorTuple } from '../../lib/promise-value-error-tuple.js'
import { toCanonicalUrl } from '../../lib/to-canonical-url.js'
import { urlHasNoCache } from '../../lib/url-has-no-cache.js'
import { wrapError } from '../../lib/wrap-error.js'
import { loader as rootLoader } from '../../root.js'
import { ErrorPage } from '../error-page.js'
import { fetchPage } from './page-service.js'
import { pagesSitemapQuery } from './queries.js'

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  let { slug, parent } = params

  if (!slug && !!parent) {
    slug = parent
    parent = undefined
  }

  slug ||= 'home'

  const preview = await isPreviewMode(request)
  const canonicalUrl = toCanonicalUrl(request.url)
  const disableCache = preview || urlHasNoCache(request.url)

  const [page, pageError] = await promiseValueErrorTuple(
    withCache(
      `loader:pages:${slug}:${parent}`,
      () => fetchPage({ request, canonicalUrl, slug, parent, draft: preview, disableCache }),
      {
        disable: disableCache,
      },
    ),
  )

  if (pageError instanceof Response) {
    throw pageError
  }

  if (!pageError && !page) {
    throw new Response('Not Found', { status: 404 })
  }

  if (pageError) {
    captureError(wrapError(`(${parent}).(${slug}) fetch page error`, pageError))
  }

  return {
    canonicalUrl,
    slug,
    page,
    parent,
    pageError,
  }
}

export default function RootSlug({ aboveFooter }: { aboveFooter?: ReactNode }) {
  const data = useLoaderData<typeof loader>()
  const { header, footer } = useRouteLoaderData<typeof rootLoader>('root')!
  const { page, pageError } = data

  if (pageError) {
    return <ErrorPage />
  }

  const { hero, content, customHeader, customFooter, offers } = page!

  return (
    <>
      <InteractionContextProvider elementLocation="header">
        <Header header={header} customHeader={customHeader} />
      </InteractionContextProvider>
      <OffersProvider offers={offers}>
        <div className={cn('w-full grow', { 'pt-800': getFragmentData(heroFragment, hero)?.type === 'none' })}>
          <InteractionContextProvider elementLocation="hero">
            <Hero data={hero} />
          </InteractionContextProvider>
          <InteractionContextProvider elementLocation="body">
            <Blocks data={content} />
            {aboveFooter}
          </InteractionContextProvider>
        </div>
      </OffersProvider>
      <InteractionContextProvider elementLocation="footer">
        <Footer footer={footer ?? null} customFooter={customFooter} />
      </InteractionContextProvider>
    </>
  )
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return data?.page?.compiledMeta ?? []
}

export const sitemap: SitemapFunction = async () => {
  const resp = await cmsGqlRequest({
    query: pagesSitemapQuery,
  })

  if (!resp.data.CmsPages?.docs?.length) {
    return []
  }

  return resp.data.CmsPages.docs.map((_page) => {
    const page = _page as NonNullable<typeof _page>
    return {
      loc: pagePathname(pagesCollection, page),
      lastmod: page.updatedAt ?? undefined,
    } as const
  })
}
